import { useEffect, useMemo, useReducer, useRef } from 'react';
import { reducer } from './reducer';
import { serialize } from './state';
import { saveLocal } from './storage';
import { schedulePush } from './sync';
import type { GameState, InputConfig } from './types';

interface Options {
  /** Reads the latest settings each time an input mutates the board. */
  getConfig: () => InputConfig;
  /** Called once when the puzzle is solved. */
  onSolved?: (state: GameState) => void;
  /** Persist locally + sync to the server on change (default true). */
  persist?: boolean;
}

const TICK_MS = 1000;

export function useGame(initial: GameState, options: Options) {
  const [state, dispatch] = useReducer(reducer, initial);
  const getConfig = options.getConfig;
  const onSolvedRef = useRef(options.onSolved);
  onSolvedRef.current = options.onSolved;
  const persist = options.persist ?? true;

  // --- Timer: tick while running; pause when the tab is backgrounded. ---
  useEffect(() => {
    if (!state.running) return;
    const id = setInterval(() => dispatch({ type: 'tick', ms: TICK_MS }), TICK_MS);
    return () => clearInterval(id);
  }, [state.running]);

  useEffect(() => {
    const onVisibility = () =>
      dispatch({ type: 'setRunning', running: document.visibilityState === 'visible' });
    const onBlur = () => dispatch({ type: 'setRunning', running: false });
    const onFocus = () => dispatch({ type: 'setRunning', running: true });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // --- Fire onSolved once. ---
  const solvedFired = useRef(false);
  useEffect(() => {
    if (state.status === 'solved' && !solvedFired.current) {
      solvedFired.current = true;
      onSolvedRef.current?.(state);
    }
  }, [state]);

  // --- Persist on meaningful change. ---
  useEffect(() => {
    if (!persist) return;
    const stored = serialize(state, Date.now());
    saveLocal(stored);
    schedulePush(stored);
  }, [persist, state.cells, state.notes, state.elapsedMs, state.status, state.hintsUsed]);

  const actions = useMemo(
    () => ({
      select: (index: number) => dispatch({ type: 'select', index }),
      move: (dir: 'up' | 'down' | 'left' | 'right') => dispatch({ type: 'move', dir }),
      input: (value: number) => dispatch({ type: 'input', value, config: getConfig() }),
      erase: () => dispatch({ type: 'erase' }),
      toggleNotesMode: () => dispatch({ type: 'toggleNotesMode' }),
      autoNotes: () => dispatch({ type: 'autoNotes' }),
      undo: () => dispatch({ type: 'undo' }),
      redo: () => dispatch({ type: 'redo' }),
      pause: () => dispatch({ type: 'setRunning', running: false }),
      resume: () => dispatch({ type: 'setRunning', running: true }),
      useHint: () => dispatch({ type: 'useHint' }),
      placeHint: (index: number, value: number) =>
        dispatch({ type: 'placeHint', index, value, config: getConfig() }),
    }),
    [getConfig],
  );

  return { state, actions };
}

export type GameActions = ReturnType<typeof useGame>['actions'];
